import { connect, createRoot, type Remix } from "@remix-run/dom";
import { events } from "@remix-run/events";
import { Drummer } from "./drummer.ts";
import { tempoTap } from "./tempo-event.ts";
import { arrowDown, arrowUp, space } from "@remix-run/events/key";
import { press } from "@remix-run/events/press";
import {
  Button,
  ControlGroup,
  createEnvelopeLoop,
  EqualizerBar,
  Layout,
  TempoButton,
} from "./components.tsx";

function DrumMachine(this: Remix.Handle<Drummer>) {
  let drummer = new Drummer(80);

  events(drummer, [Drummer.change(() => this.render())]);

  events(document, [
    space(() => {
      drummer.toggle();
    }),
    arrowUp(() => {
      drummer.setTempo(drummer.bpm + 1);
    }),
    arrowDown(() => {
      drummer.setTempo(drummer.bpm - 1);
    }),
  ]);

  this.context.set(drummer);

  return () => (
    <Layout>
      <Equalizer />
      <DrumControls />
    </Layout>
  );
}

export function Equalizer(this: Remix.Handle) {
  let drummer = this.context.get(DrumMachine);

  let kickVolumes = [0.4, 0.8, 0.3, 0.1];
  let snareVolumes = [0.4, 1, 0.7];
  let hatVolumes = [0.1, 0.8];

  let { createEnvelope } = createEnvelopeLoop(() => this.render());

  let kickEnv = createEnvelope(220);
  let snareEnv = createEnvelope(280);
  let hatEnv = createEnvelope(120);

  events(drummer, [
    Drummer.kick(() => {
      kickEnv.trigger(1);
    }),
    Drummer.snare(() => {
      snareEnv.trigger(1);
    }),
    Drummer.hat(() => {
      hatEnv.trigger(1);
    }),
  ]);

  return () => {
    let kicks = kickVolumes.map(volume => kickEnv.value * volume);
    let snares = snareVolumes.map(volume => snareEnv.value * volume);
    let hats = hatVolumes.map(volume => hatEnv.value * volume);

    return (
      <div
        css={{
          display: "flex",
          background: "black",
          borderRadius: "24px",
          padding: "24px",
          height: 452,
          gap: "4px",
        }}
      >
        {/* kick */}
        <EqualizerBar volume={kicks[0]} />
        <EqualizerBar volume={kicks[1]} />
        <EqualizerBar volume={kicks[2]} />
        <EqualizerBar volume={kicks[3]} />

        {/* snare */}
        <EqualizerBar volume={snares[0]} />
        <EqualizerBar volume={snares[1]} />
        <EqualizerBar volume={snares[2]} />

        {/* hat */}
        <EqualizerBar volume={hats[0]} />
        <EqualizerBar volume={hats[1]} />
      </div>
    );
  };
}

function DrumControls(this: Remix.Handle) {
  let drummer = this.context.get(DrumMachine);
  let stop: HTMLButtonElement;
  let play: HTMLButtonElement;

  events(drummer, [Drummer.change(() => this.render())]);

  return () => (
    <ControlGroup
      css={{
        "& button:focus-visible": {
          outline: "2px solid #2684FF",
          outlineOffset: "2px",
        },
      }}
    >
      <Button
        on={[
          tempoTap(event => {
            drummer.play(event.detail);
          }),
        ]}
      >
        SET TEMPO
      </Button>
      <TempoDisplay />
      <Button
        disabled={drummer.isPlaying}
        on={[
          connect(event => (play = event.currentTarget)),
          press(event => {
            drummer.play();
            this.queueTask(() => {
              stop.focus();
            });
          }),
        ]}
      >
        PLAY
      </Button>
      <Button
        disabled={!drummer.isPlaying}
        on={[
          connect(event => (stop = event.currentTarget)),
          press(() => {
            drummer.stop();
            this.queueTask(() => {
              play.focus();
            });
          }),
        ]}
      >
        STOP
      </Button>
    </ControlGroup>
  );
}

function TempoDisplay(this: Remix.Handle) {
  let drummer = this.context.get(DrumMachine);
  return () => (
    <div
      css={{
        display: "flex",
        flexDirection: "row",
        gap: "10px",
        alignItems: "flex-end",
        height: 160,
      }}
    >
      <div
        css={{
          height: "100%",
          display: "flex",
          flex: 1,
          background: "#0B1B05",
          color: "#64C146",
          padding: "42px",
          borderTopLeftRadius: "24px",
          borderBottomLeftRadius: "24px",
          alignItems: "end",
        }}
      >
        <div
          css={{
            fontSize: "24px",
            fontWeight: 700,
            width: "33%",
          }}
        >
          BPM
        </div>
        <div
          css={{
            flex: 1,
            fontSize: "92px",
            fontWeight: 700,
            position: "relative",
            top: 22,
            textAlign: "right",
            fontFamily: "JetBrains Mono, monospace",
          }}
        >
          {drummer.bpm}
        </div>
      </div>
      <div
        css={{
          width: 75,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          height: "100%",
          justifyContent: "space-between",
        }}
      >
        <TempoButton
          css={{ borderTopRightRadius: "24px" }}
          orientation="up"
          on={press(() => {
            drummer.setTempo(drummer.bpm + 1);
          })}
        />
        <TempoButton
          css={{ borderBottomRightRadius: "24px" }}
          orientation="down"
          on={press(() => {
            drummer.setTempo(drummer.bpm - 1);
          })}
        />
      </div>
    </div>
  );
}

createRoot(document.body).render(<DrumMachine />);
